import { Injectable, Injector, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';
import { ClickAnalyticsPlugin } from '@microsoft/applicationinsights-clickanalytics-js';
import { UserDto } from '@app/generated/model';
import { loadManifest } from '@app/shared/utils/manifest';
import { getDeviceInfo } from '@app/core/utils/device-info.util';
import { env } from '../../../environments/env';

export type TelemetryProperties = Record<string, unknown>;

export interface TelemetryScope {
  dispose(): void;
}

/** The SDK sends numbers as customMeasurements; stringify so every property lands in customDimensions. */
export function toCustomDimensions(properties: TelemetryProperties): Record<string, string> {
  const dimensions: Record<string, string> = {};
  for (const [name, value] of Object.entries(properties)) {
    if (value === undefined || value === null) {
      continue;
    }
    dimensions[name] =
      typeof value === 'string' ? value : typeof value === 'object' ? JSON.stringify(value) : String(value);
  }
  return dimensions;
}

@Injectable({
  providedIn: 'root',
})
export class AppInsightsService implements OnDestroy {
  private appInsights?: ApplicationInsights;
  private consoleSink = false;
  private consoleSinkSubscription?: Subscription;
  private environmentName = '';
  private authenticatedUserId?: string;
  private readonly globalProperties = new Map<string, unknown>();
  private readonly scopes: TelemetryProperties[] = [];

  // Router is resolved lazily: it is only needed by the console sink.
  constructor(private readonly injector: Injector) {}

  init(): void {
    const { appInsights: config, name } = env.get();
    if (this.appInsights || this.consoleSink) {
      return;
    }
    this.environmentName = name;

    if (config.sink === 'console') {
      this.startConsoleSink();
      return;
    }
    if (!config.connectionString) {
      return;
    }

    const clickPlugin = new ClickAnalyticsPlugin();
    this.appInsights = new ApplicationInsights({
      config: {
        connectionString: config.connectionString,
        enableAutoRouteTracking: true,
        // The backend does not report to App Insights, so don't add traceparent/Request-Id headers to API calls.
        disableCorrelationHeaders: true,
        // `unload` listeners trigger a permissions-policy violation; the SDK still flushes on pagehide/beforeunload.
        disablePageUnloadEvents: ['unload'],
        extensions: [clickPlugin],
        extensionConfig: {
          [clickPlugin.identifier]: {
            autoCapture: true,
            dropInvalidEvents: true,
            // Only data-ai-* tagged elements are named; the default would send visible element text.
            dataTags: { customDataPrefix: 'data-ai-', useDefaultContentNameOrId: false },
          },
        },
      },
    });
    this.appInsights.loadAppInsights();
    this.appInsights.addTelemetryInitializer((item) => {
      item.tags = { ...item.tags, 'ai.cloud.role': 'dwo-client' };
      item.data = { ...this.getContextProperties(), ...item.data, environment: name };
    });

    const manifest = loadManifest();
    if (manifest) {
      this.appInsights.context.application.ver = `${manifest.timestamp} - ${manifest.revision}`;
    }
  }

  setUser(user: UserDto): void {
    this.authenticatedUserId = user.sapId;
    if (user.sapId) {
      // Cookie keeps the id on telemetry sent after a reload, before the profile is fetched again.
      this.appInsights?.setAuthenticatedUserContext(user.sapId, undefined, true);
    }
    this.setProperty('SapId', user.sapId);
    this.setProperty('Role', user.role);
    this.setProperty('ProjectIds', user.projects?.map((project) => project.id).join(','));
    this.setProperty('Sites', user.sites?.join(','));
  }

  clearUser(): void {
    this.authenticatedUserId = undefined;
    this.appInsights?.clearAuthenticatedUserContext();
    this.globalProperties.clear();
    this.appInsights?.flush();
  }

  /** Sets an app-wide property on all telemetry until replaced; `undefined` removes it. */
  setProperty(name: string, value: unknown): void {
    if (value === undefined) {
      this.globalProperties.delete(name);
    } else {
      this.globalProperties.set(name, value);
    }
  }

  /** Adds properties to all telemetry until the returned scope is disposed. App-wide, not async-local. */
  pushProperties(properties: TelemetryProperties): TelemetryScope {
    const scope = { ...properties };
    this.scopes.push(scope);
    return {
      dispose: () => {
        const index = this.scopes.indexOf(scope);
        if (index >= 0) {
          this.scopes.splice(index, 1);
        }
      },
    };
  }

  getContextProperties(): Record<string, string> {
    return toCustomDimensions(
      Object.assign({ ...getDeviceInfo() }, Object.fromEntries(this.globalProperties), ...this.scopes),
    );
  }

  trackException(
    exception: Error,
    properties?: TelemetryProperties,
    severityLevel: SeverityLevel = SeverityLevel.Error,
  ): void {
    const dimensions = toCustomDimensions(properties ?? {});
    if (this.consoleSink) {
      this.writeToConsole('exception', exception.message, severityLevel, dimensions, exception);
      return;
    }
    this.appInsights?.trackException({ exception, severityLevel }, dimensions);
  }

  trackEvent(name: string, properties?: TelemetryProperties): void {
    const dimensions = toCustomDimensions(properties ?? {});
    if (this.consoleSink) {
      this.writeToConsole('customEvent', name, SeverityLevel.Information, dimensions);
      return;
    }
    this.appInsights?.trackEvent({ name }, dimensions);
  }

  trackTrace(
    message: string,
    severityLevel: SeverityLevel = SeverityLevel.Information,
    properties?: TelemetryProperties,
  ): void {
    const dimensions = toCustomDimensions(properties ?? {});
    if (this.consoleSink) {
      this.writeToConsole('trace', message, severityLevel, dimensions);
      return;
    }
    this.appInsights?.trackTrace({ message, severityLevel }, dimensions);
  }

  private startConsoleSink(): void {
    this.consoleSink = true;
    this.consoleSinkSubscription = this.injector
      .get(Router)
      .events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => this.writeToConsole('pageView', event.urlAfterRedirects, SeverityLevel.Information, {}));
    document.addEventListener('click', this.onConsoleClick, true);
  }

  ngOnDestroy(): void {
    this.consoleSinkSubscription?.unsubscribe();
    document.removeEventListener('click', this.onConsoleClick, true);
  }

  /** Approximates the click analytics plugin: nearest `data-ai-id` element, all its `data-ai-*` attributes. */
  private readonly onConsoleClick = (event: MouseEvent): void => {
    const element = (event.target as Element | null)?.closest?.('[data-ai-id]');
    if (!element) {
      return;
    }
    const content: Record<string, string> = {};
    for (const attribute of Array.from(element.attributes)) {
      if (attribute.name.startsWith('data-ai-')) {
        content[attribute.name.slice('data-ai-'.length)] = attribute.value;
      }
    }
    this.writeToConsole('click', content['id'], SeverityLevel.Information, { content: JSON.stringify(content) });
  };

  private writeToConsole(
    type: string,
    name: string,
    severityLevel: SeverityLevel,
    dimensions: Record<string, string>,
    exception?: Error,
  ): void {
    const item = {
      name,
      user_AuthenticatedId: this.authenticatedUserId,
      customDimensions: { ...this.getContextProperties(), ...dimensions, environment: this.environmentName },
    };
    const args: unknown[] = [`[Telemetry] ${type} ${name}`, item, ...(exception ? [exception] : [])];

    switch (severityLevel) {
      case SeverityLevel.Verbose:
        console.debug(...args);
        break;
      case SeverityLevel.Warning:
        console.warn(...args);
        break;
      case SeverityLevel.Error:
      case SeverityLevel.Critical:
        console.error(...args);
        break;
      default:
        console.info(...args);
    }
  }
}
